import { ProductUtil } from "@/utils/ProductUtil";
import type { Product } from "@/api/product-api";
import type { BadgeType } from "@/v2-components/global-navigation-bar/_const/navigation-menus";

/**
 * GNB Store 패널의 **문구·순서·그룹** 정의 (#5415).
 *
 * ## 왜 별도 모듈인가
 *
 * 데스크톱(`navigation-menu-content.tsx`)과 모바일(`navigation-menu-sheet/menu-view.tsx`)이
 * 각자 하드코딩하는 구조라, 이 리포는 같은 자리에서 **반복해서 갈라졌다** — Support 메뉴가
 * 그랬고(#5415), 게임 패널 라벨이 그랬다. 시안(`29212:35770` 데스크톱 ·
 * `29303:21604` 모바일)이 **같은 문구·같은 순서·같은 3그룹**을 그리고 있으므로,
 * 레이아웃만 화면별로 두고 데이터는 여기 한 곳에 둔다.
 *
 * 화면별로 다른 것은 **치수뿐이다** — 썸네일 72 vs 56, 좌인셋 12 vs 36 등.
 */

/**
 * 시안이 제품 행에 새로 넣은 정적 문구. 제품 데이터에 없다 — `shortDesc` 는 영문 기술
 * 설명("The ultimate haptic vest with 32 feedback points")이고 `tags` 는 `new`/`sale`
 * **배지**라 축이 다르다. 그래서 slug 로 매핑한다.
 *
 * ## ★`highlight`(녹색 라벨)와 `tags`(배지)를 걷었다 (2026-09-03)
 *
 * 시안이 그 요소들을 **`hidden="true"` 로 껐다** — 지운 게 아니라 숨긴 것이라
 * `get_metadata` 로만 보인다:
 *
 * ```
 *   29229:21391  "Best for PC/VR games"          hidden=true
 *   29229:21398  "Best for PC games"             hidden=true
 *   29229:21409  tags(VR Gaming · Sim Racing)    hidden=true
 *   29229:21429  tags(VR Gaming)                 hidden=true
 * ```
 *
 * 그 자리에 시안은 **한 줄 설명**을 쓴다(`29229:21392` 등, 종전엔 이쪽이 안 보이던
 * 형제였다). 실측(2026-09-03 1440px): 우리 화면엔 녹색 라벨 2개와 배지 3개가 그대로
 * 떠 있었다. `highlight`·`tags` 를 타입에서 없애 **데스크탑·모바일 양쪽이 함께** 정리된다.
 *
 * ## ★썸네일은 **시안 우선**이다 (2026-09-03)
 *
 * 제품 API 의 `product.thumbnail` 과 시안 썸네일이 **5장 중 2장 달랐다**(실측 대조,
 * `screenshots/thumb-compare.png`):
 *
 * ```
 *   TactSuit Air    시안 검정+빨강   API air-white-red.png  흰색+빨강
 *   TactGlove       시안 한 짝       API …charcoal-0a.png   두 짝(한 켤레)
 * ```
 *
 * 나머지 3장(Pro · Sleeve · Visor)은 같다. 시안이 정본이므로 5장을 리포가 소유하고
 * (`public/images/gnb/store/`, Figma 원본 ~502² → 288² webp q90 · 합계 43KB),
 * **`thumb` 가 있으면 그것을 쓰고 없으면 `product.thumbnail` 로 떨어진다.**
 *
 * 288 인 이유: 시안 썸네일이 데스크탑 **72** · 모바일 **56** 이라 @2x 로도 144 면
 * 충분한데, 여유를 두되 원본 502² 를 그대로 담지 않는다.
 *
 * ⚠️ **시안에 없는 제품은 설명 없이 이름만 나온다.** 시안은 5종만 그렸는데 실제로는
 * `TactSuit Air: Camo` · `Tactosy for Feet` 도 판매 중이다. 빈 문구를 지어내지 않는다 —
 * 문구가 정해지면 여기 한 줄 추가하면 두 화면에 함께 반영된다.
 *
 * ★**키 순서가 곧 표시 순서다**(`bySpecOrder`). 시안 읽는 순서와 같게 유지할 것.
 */
const THUMB_BASE = "/images/gnb/store";

/**
 * ★**두 화면의 문구가 갈라졌다** (#5605 · 2026-09-08 실측).
 *
 * 이 파일 위 주석은 *"시안이 **같은 문구·같은 순서·같은 3그룹**을 그리고 있으므로"* 를
 * 근거로 단일 출처를 만들었는데, **그 전제가 더는 참이 아니다.** 모바일 시안만 단순화됐다:
 *
 * ```
 *                    데스크탑 `29212:35770`                    모바일 `29303:21604`
 *   그룹 라벨 3개    보임                                      **hidden=true**
 *   TactSuit Pro     Premium haptic suit for PC and VR gaming  Premium haptic suit
 *   TactSuit Air     Lightweight haptic suit optimized for …   Lightweight haptic suit
 *   TactSleeve       Arm haptics for VR gaming and sim racing  Arm haptics
 *   TactGlove        Fingertip haptics for XR and training     Fingertip to palm haptics
 *   TactVisor        Face haptics through your headset         (같음)
 * ```
 *
 * 그래서 `descMobile` 을 **다를 때만** 둔다 — 없으면 `desc` 로 떨어진다(TactVisor 가 그렇다).
 * 두 값을 늘 채우면 같은 문구가 두 벌이 되어 한쪽만 고치는 사고가 난다.
 */
export const STORE_COPY: Record<
  string,
  { desc: string; descMobile?: string; thumb?: string }
> = {
  "tactsuit-pro": {
    desc: "Premium haptic suit for PC and VR gaming",
    descMobile: "Premium haptic suit",
    thumb: `${THUMB_BASE}/pro@2x.webp`,
  },
  "tactsuit-air": {
    desc: "Lightweight haptic suit optimized for PC gaming",
    descMobile: "Lightweight haptic suit",
    thumb: `${THUMB_BASE}/air@2x.webp`,
  },
  /**
   * ★**시안에 없는 제품인데 문구를 받았다** (#5559 · 작성자 2026-09-08:
   * *"카모 유지해주시고 밑에 문구만 추가 부탁드립니다! Camouflage style for FPS gaming"*).
   *
   * 위 ⚠️ 가 *"문구가 정해지면 여기 한 줄 추가하면 두 화면에 함께 반영된다"* 로 예고한
   * 자리다. 실제로 데스크탑·모바일이 같은 `STORE_COPY[product.slug]` 를 읽는다
   * (`navigation-menu-content.tsx:248` · `menu-view.tsx:398`).
   *
   * ⚠️ **`thumb` 을 두지 않는다** — 시안이 이 제품 썸네일을 안 그렸다. 없으면 호출처가
   * `product.thumbnail`(API)로 떨어진다. 없는 자산 경로를 적으면 깨진 이미지가 된다.
   *
   * ⚠️ **`descMobile` 도 두지 않는다** — 모바일 시안에 이 카드가 없어 줄인 문구의 정본이
   * 없다. 위 규칙대로 **다를 때만** 두고, 없으면 `desc` 로 떨어진다(TactVisor 와 같다).
   *
   * ★**키를 여기(Air 다음)에 넣으면 표시 순서가 바뀐다** — `bySpecOrder` 가 이 객체의
   * **키 순서**를 쓰고, 종전엔 카모가 표에 없어 맨 뒤(`rank = order.length`)로 밀려
   * 있었다. 시안의 가족 묶음(Pro · Air · Camo)과 맞는 자리라 여기 둔다.
   */
  "tactsuit-air-camo": {
    desc: "Camouflage style for FPS gaming",
  },
  tactsleeve: {
    desc: "Arm haptics for VR gaming and sim racing",
    descMobile: "Arm haptics",
    thumb: `${THUMB_BASE}/sleeve@2x.webp`,
  },
  "tactglove-dk3": {
    desc: "Fingertip haptics for XR and training",
    descMobile: "Fingertip to palm haptics",
    thumb: `${THUMB_BASE}/glove@2x.webp`,
  },
  tactvisor: {
    desc: "Face haptics through your headset",
    thumb: `${THUMB_BASE}/visor@2x.webp`,
  },
};

/**
 * 시안이 그린 순서로 정렬한다 — 시안 확장 기어 그룹은
 * `TactSleeve → TactGlove → TactVisor` 인데 `categorizeDevices` 는 신체부위 taxonomy
 * 순이라 `TactVisor` 가 앞으로 온다.
 *
 * ★순서의 출처를 `STORE_COPY` 한 곳으로 묶는다. 별도 순서 배열을 두면 문구를 추가할 때
 * 한쪽만 고쳐 갈라진다. 표에 없는 제품은 **데이터 순서 그대로 뒤에** 붙는다(`sort` 는 stable).
 */
export const bySpecOrder = (products: Product[]): Product[] => {
  const order = Object.keys(STORE_COPY);
  const rank = (p: Product) => {
    const i = order.indexOf(p.slug);
    return i === -1 ? order.length : i;
  };
  return [...products].sort((a, b) => rank(a) - rank(b));
};

/** 시안 그룹 라벨 3개. 번역 키이자 표시 문구다. */
export const STORE_GROUP_LABELS = {
  /**
   * ★**설명 절을 걷었다** (작성자 2026-09-08: *"- Main gear for full-body immersion,
   * - Add haptics to arms, hands, and face 는 지우기로 했다. 길어서"*).
   *
   * 영문 원문이 곧 키라서 **7 로케일 키가 함께 바뀌었다.** 각 로케일의 구분자가 달라
   * (` - ` en·cn·jp · ` – ` de·fr · `: ` es · ` — ` kr) 문자열이 아니라 **성질**로
   * 잘라 앞부분만 남겼다.
   *
   * ★**셋 다 걷었다.** 처음엔 작성자가 앞의 둘만 지목해 `lineup` 을 남겼는데(같은 모양·
   * 비슷한 길이라 후보로만 적어 뒀다), 그 상태가 **짧은 라벨 둘 + 긴 라벨 하나**로
   * 섞여 있었다. 확인 후 `lineup` 도 같은 술어로 걷었다.
   */
  torso: "Haptic Suits",
  extras: "Expansion Gear",
  lineup: "Full Lineup",
} as const;

/**
 * 시안 「전체 라인업」 그룹의 링크 3개.
 *
 * ⚠️ **정적 경로만 둔다.** 활성 판정에 경로 비교를 쓰는데 동적 라우트(PDP)에서는
 * 성립하지 않는다(#5386). 제품은 `bySpecOrder` 로 따로 그린다.
 */
export const STORE_LINEUP_LINKS = [
  { title: "Bundle Set", href: "/tactsuit/bundles" },
  { title: "Accessories", href: "/tactsuit/accessories" },
  { title: "See all products", href: "/tactsuit", cta: true },
] as const;

/**
 * 시안의 두 제품 그룹으로 가른다. **신체부위 taxonomy 와 축이 다르다** — 몸통이냐
 * 아니냐로만 나눈다.
 */
/**
 * ★**단종 제품은 GNB 에 안 띄운다** (2026-09-03).
 *
 * `Tactosy for Feet` 이 단종인데 admin 에서 **「판매중지」가 아니라 재고 0** 으로만 잡혀
 * 공개 API 에 그대로 온다 — 판매중지였다면 목록에서 아예 빠졌겠지만 품절은 남는다.
 * (실측 2026-09-04 `/api/products/?country=US` **dev**: `isActive=true` ·
 * `stockInfo.baseStock === 0`. 이 값은 「단종이라 0」이지 재고 변동이 아니라서 위 두
 * 수치와 달리 시점에 안 흔들린다 — 그래도 환경을 적어 둔다.)
 *
 * ## ⚠️ 재고 축으로 거르지 않는다 — 품절까지 숨는다
 *
 * 처음엔 `stockInfo.baseStock !== 0` 으로 걸렀는데 그건 **「단종」이 아니라 「품절」**
 * 술어다. 재고가 소진되면 그 제품이 **말없이** 메뉴에서 빠지는데 PDP 는 살아 있어서,
 * 사이트에 존재하지만 내비게이션으로는 못 가는 상태가 된다. 커머스에서 품절은 보통
 * 숨기는 게 아니라 **품절 상태로 보여 주는** 축이다.
 *
 * ⚠️ **재고 값을 여기 적지 않는다.** 시점마다 바뀌고 **국가별로도 다르며**(같은 slug 가
 * `country=US` 와 `KR` 에서 값이 갈린다) **환경별로도 다르다**(dev API 와 prod 가 서로
 * 다른 수를 준다). 실제로 이 주석의 초판에 적었던 수치가 그 셋 때문에 재현되지 않았다.
 * 필요하면 그때 직접 잰다:
 *
 * ```
 *   curl -s "<앱 호스트>/api/products/?country=US" | jq '.data[] | select(.productType=="DEVICE")
 *     | {slug, stock: .stockInfo.baseStock}'
 * ```
 *
 * 재고 축이 틀린 이유는 「어느 제품이 몇 개냐」가 아니라 **그 값이 움직인다**는 것이므로
 * 수치 없이도 논거가 선다 — 국가·환경별로도 갈린다는 사실이 오히려 그 논거를 강화한다.
 *
 * ## 그래서 slug 로 명시한다
 *
 * API 로는 단종·품절이 안 갈린다. 재고는 **시점 의존**이라 필터 축으로 쓰면 메뉴가
 * 재고 변동에 흔들린다 — 지금 필요한 건 「이 제품 하나」이므로 그것만 적는다.
 *
 * ⚠️ **다음 단종 때 여기 한 줄이 는다.** 그게 반복되면 admin 에 **단종 플래그**를
 * 요청하는 것이 정공법이다(그때 이 목록을 걷는다).
 */
const DISCONTINUED_SLUGS: readonly string[] = ["tactosy-for-feet"];

const isListed = (p: Product) => !DISCONTINUED_SLUGS.includes(p.slug);

export const splitStoreGroups = (products: Product[] | undefined) => {
  const categorized = ProductUtil.categorizeDevices(
    (products ?? []).filter(isListed),
  );
  return {
    torso: bySpecOrder(
      categorized.find((c) => c.category === "Torso")?.products ?? [],
    ),
    extras: bySpecOrder(
      categorized
        .filter((c) => c.category !== "Torso")
        .flatMap((c) => c.products),
    ),
  };
};

/**
 * Store 패널 **제품 행**의 배지 (#5603 — *"그 외로 세일하는 항목에는 sale 딱지 노출"*).
 *
 * ## `Sale` 하나뿐이다
 *
 * `new` 는 붙이지 않는다 — 요청이 `sale` 만이고, prod 실측(2026-09-08 `admin-apis
 * /api/v1/shop-products` 37건)에서 `new` 는 액세서리 11건 + 디바이스 2건에 걸려 있어
 * 붙이면 패널이 배지로 덮인다.
 *
 * ## ★두 화면이 **같은 술어**를 읽는다
 *
 * 데스크탑(`navigation-menu-content.tsx`)과 모바일(`menu-view.tsx`)이 각자 조건을 쓰면
 * 한쪽만 고치는 사고가 난다 — 이 패널은 이미 문구·순서·썸네일을 이 파일 한 곳에서 읽고
 * 있으므로 배지도 같은 자리에 둔다.
 *
 * ⚠️ **시안에는 제품 행 배지가 없다**(모바일 `29303:21607` 등 · 데스크탑 `29229:21386` 등).
 *    그건 시안을 그린 날 그 5종에 `sale` 이 없었기 때문이고, 배지는 라이브 커머스 신호다.
 *    실측 기준 지금 걸리는 것은 `tactvisor` **한 건**이다.
 */
export const storeRowBadge = (product: Product): BadgeType | undefined =>
  product.tags?.includes("sale") ? "sale" : undefined;
